import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { Meta } from '../../db/tipos'
import { ROTULOS_TIPO_META } from '../../metas/rotulos'
import {
  lerConfiguracao, gravarConfiguracao, PADRAO_METAS, TODOS_OS_TIPOS, type ConfigDeMetas,
} from '../../metas/configuracao'
import { useSalvamento } from '../../componentes/useSalvamento'
import BotaoSalvar from '../../componentes/BotaoSalvar'

const CAMPO = 'w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm'

export default function ConfiguracaoMetas() {
  const navegar = useNavigate()
  const [config, setConfig] = useState<ConfigDeMetas>(PADRAO_METAS)
  // Enquanto ela nao mexeu em nada, a tela segue o que vier do servidor. Depois
  // que mexeu, para de seguir -- senao uma sincronizacao apagaria o que ela
  // esta digitando.
  const [tocado, setTocado] = useState(false)
  const { estado, executar, reiniciar } = useSalvamento()

  // ⚠ TEM de ser useLiveQuery, nao uma leitura unica no mount. Num aparelho que
  // ainda nao sincronizou (celular novo, cache limpo), a linha do servidor chega
  // DEPOIS que a tela montou. Com leitura unica a tela mostrava o padrao para
  // sempre, e ao salvar criava uma SEGUNDA linha -- que o indice unico por
  // tenant recusa, deixando a gravacao presa na fila em silencio.
  // Achado pelo E2E em 2026-08-05, nao por inspecao.
  const linhasGravadas = useLiveQuery(
    () => banco.configuracao_metas.filter((c) => c.apagado_em === null).toArray(),
    [],
  )

  useEffect(() => {
    if (linhasGravadas === undefined || tocado) return
    void lerConfiguracao().then(setConfig)
  }, [linhasGravadas, tocado])

  // Editar um campo faz o que esta na tela deixar de ser o que foi gravado, e
  // manter "Salvo" seria mentira -- mesma regra dos outros formularios.
  function mudar(alteracao: Partial<ConfigDeMetas>) {
    reiniciar()
    setTocado(true)
    setConfig((atual) => ({ ...atual, ...alteracao }))
  }

  function alternarTipo(tipo: Meta['tipo']) {
    reiniciar()
    setTocado(true)
    setConfig((atual) => ({
      ...atual,
      tiposAtivos: atual.tiposAtivos.includes(tipo)
        ? atual.tiposAtivos.filter((t) => t !== tipo)
        : [...atual.tiposAtivos, tipo],
    }))
  }

  return (
    <section className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Configuração das metas</h2>
      <p className="text-sm text-slate-500">
        Isto é opcional. Sem mexer aqui, o criador já sugere com valores padrão.
      </p>

      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <h3 className="font-medium text-slate-800 text-sm">Quanto quero crescer</h3>

        <label className="block">
          <span className="block text-xs text-slate-500 mb-0.5">Metas de dinheiro (%)</span>
          <input
            data-testid="pct-dinheiro" type="number" className={CAMPO}
            value={config.pctDinheiro}
            onChange={(e) => mudar({ pctDinheiro: Number(e.target.value) })}
          />
        </label>

        <label className="block">
          <span className="block text-xs text-slate-500 mb-0.5">
            Cobertura de linhas (pontos — nunca passa de 100%)
          </span>
          <input
            data-testid="pontos-cobertura" type="number" className={CAMPO}
            value={config.pontosCobertura}
            onChange={(e) => mudar({ pontosCobertura: Number(e.target.value) })}
          />
        </label>

        <label className="block">
          <span className="block text-xs text-slate-500 mb-0.5">Itens por pedido (passo)</span>
          <input
            data-testid="passo-itens" type="number" step="0.1" className={CAMPO}
            value={config.passoItensPorPedido}
            onChange={(e) => mudar({ passoItensPorPedido: Number(e.target.value) })}
          />
        </label>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
        <h3 className="font-medium text-slate-800 text-sm">Quais metas eu uso</h3>
        {TODOS_OS_TIPOS.map((tipo) => (
          <label key={tipo} className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox" data-testid={`tipo-${tipo}`}
              checked={config.tiposAtivos.includes(tipo)}
              onChange={() => alternarTipo(tipo)}
            />
            {ROTULOS_TIPO_META[tipo]}
          </label>
        ))}
        {config.tiposAtivos.length === 0 && (
          <p className="text-xs text-amber-700">
            Sem nenhuma meta marcada o criador não tem o que sugerir.
          </p>
        )}
      </div>

      <div data-testid="salvar-config">
        <BotaoSalvar
          estado={estado}
          type="button"
          onClick={() => void executar(() => gravarConfiguracao(config))}
        >
          Salvar
        </BotaoSalvar>
      </div>

      <button
        onClick={() => navegar('/metas')}
        className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 font-medium"
      >
        Voltar
      </button>
    </section>
  )
}
